import {
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {
  RiskAssessmentData,
  RiskAssessmentType,
} from "../features/data/patientApiSlice";
import { combineSx, createStyleSheet, globalStyles } from "../utils/styles";
import {
  getRiskScoreClassification,
  getRiskScoreClassificationLongName,
  riskAssessmentColors,
} from "../utils/riskAssessments";
import { formatDateTime } from "../utils/dates";

import InfoIcon from "@mui/icons-material/InfoOutlined";
import TabView from "./TabView";
import { computeReadableValue } from "../utils/strings";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const excludeRiskScoreForTypes = [RiskAssessmentType.PhithosHIVRisk];

const RiskAssessmentDetail = (props: {
  riskAssessments: RiskAssessmentData[];
  selectedAssessmentType: RiskAssessmentType;
}) => {
  const { riskAssessments, selectedAssessmentType } = props;
  const { t } = useTranslation("Patient");

  // Component state
  const [selectedAssessment, setSelectedAssessment] = useState(
    riskAssessments.find((ra) => ra.type === selectedAssessmentType)!
  );

  // Callbacks
  const handleTabClick = (type: RiskAssessmentType) => () => {
    setSelectedAssessment(riskAssessments.find((ra) => ra.type === type)!);
  };

  const riskScoreClassification =
    getRiskScoreClassification(selectedAssessment);
  return (
    <Stack direction="column" sx={styles.container}>
      <TabView
        entries={riskAssessments.map((riskAssessment) => ({
          content: t(`riskAssessmentTypes:${riskAssessment.type}:title`),
          selected: riskAssessment.type === selectedAssessment.type,
          onClick: handleTabClick(riskAssessment.type), // No action needed, just display the content
        }))}
      />
      <Typography
        variant="detailCellBody"
        sx={{
          ...globalStyles.gapTop,
          color: riskAssessmentColors[riskScoreClassification],
        }}
      >
        {getRiskScoreClassificationLongName(t, selectedAssessment)}
        {selectedAssessment.risk_score &&
        !excludeRiskScoreForTypes.includes(selectedAssessment.type) ? (
          <>
            <Typography
              variant="detailCellBody"
              sx={combineSx(styles.inlineText, styles.separatorPipe)}
            >
              |
            </Typography>
            <Typography
              variant="detailCellBody"
              sx={{
                ...styles.inlineText,
                color: riskAssessmentColors[riskScoreClassification],
              }}
            >
              {selectedAssessment.risk_score || 0}
            </Typography>
          </>
        ) : null}
      </Typography>
      {selectedAssessment.confidence_score_classification ? (
        <>
          <Typography variant="detailCellBody" sx={globalStyles.gapTop}>
            {t("healthScoreConfidenceTitle")}
          </Typography>
          <Typography variant="body2" sx={styles.grayText}>
            {t(
              "confidenceScoreClassifications:" +
                selectedAssessment.confidence_score_classification
            )}
          </Typography>
        </>
      ) : null}
      <Typography variant="detailCellBody" sx={globalStyles.gapTop}>
        {t([
          `riskAssessmentTypes:${selectedAssessment.type}:explanationTitle`,
          "riskAssessmentTypes:common:explanationTitle",
        ])}
      </Typography>
      {(selectedAssessment.explanation?.length || 0) > 0 ? (
        <Table>
          <TableBody>
            {selectedAssessment.explanation!.map((factor) => (
              <TableRow key={factor.description}>
                <TableCell sx={{ ...styles.plainCell, width: 320 }}>
                  <Typography
                    component="ul"
                    sx={styles.descriptionTextContainer}
                  >
                    <Typography
                      variant="body2"
                      component="li"
                      sx={combineSx(styles.grayText, styles.descriptionBulletText)}
                    >
                      {computeReadableValue(factor.description)}
                    </Typography>
                  </Typography>
                </TableCell>
                <TableCell
                  sx={Object.assign(
                    {},
                    styles.plainCell,
                    styles.explanationValueCell
                  )}
                  width="max-content"
                >
                  <Typography
                    variant={factor.raw_value ? "body2bold" : "body2"}
                    sx={factor.raw_value ? undefined : styles.valueNotPresentText}
                  >
                    {(computeReadableValue(factor.raw_value || "") ||
                      t([
                        `riskAssessmentTypes:${selectedAssessment.type}:valueNotPresent`,
                        "riskAssessmentTypes:common:valueNotPresent",
                      ])) +
                      (factor.risk_score && factor.risk_score !== "0"
                        ? ` (+${factor.risk_score})`
                        : "")}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2">{t("common:unknown")}</Typography>
      )}
      <Typography variant="detailCellBody" sx={globalStyles.gapTop}>
        {t("lastAssessedTitle")}
      </Typography>
      <Typography variant="body2" sx={styles.grayText}>
        {selectedAssessment.timestamp
          ? formatDateTime(new Date(selectedAssessment.timestamp)) ||
            t("common:unknown")
          : t("common:unknown")}
      </Typography>
      <Divider sx={globalStyles.gapTop} />
      <Stack direction="row" sx={globalStyles.gapTop}>
        <InfoIcon color="primary" fontSize="small" />
        <Typography
          variant="detailCellTitle"
          sx={styles.aboutText}
          dangerouslySetInnerHTML={{
            __html: t(`riskAssessmentTypes:${selectedAssessment.type}:about`),
          }}
        />
      </Stack>
    </Stack>
  );
};

const styles = createStyleSheet({
  container: {
    padding: 1,
  },
  separatorPipe: {
    mx: 1,
  },
  inlineText: {
    display: "inline",
  },
  valueNotPresentText: {
    fontStyle: "italic",
  },
  grayText: {
    color: "GrayText",
  },
  descriptionBulletText: {
    listStyleType: "disc",
  },
  plainCell: {
    padding: 0,
    py: 0.25,
    borderBottom: "none",
    verticalAlign: "top",
  },
  descriptionTextContainer: {
    paddingLeft: 2,
  },
  explanationValueCell: {
    paddingLeft: 1,
  },
  aboutText: {
    marginLeft: 1,
    color: "primary.main",
  },
});

export default RiskAssessmentDetail;
